import { useState } from 'react';
import { formatDate } from '../lib/dates';
import { api, ApiError, asList } from '../lib/api';
import { remarksApi, REMARK_KINDS, type Remark } from '../lib/downloadsApi';

/** Staff page to record and review remarks on a student. */
export function RemarksStaffPage() {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Array<{ id: string; fullName: string; admissionNumber: string; classLabel?: string | null }>>([]);
  const [student, setStudent] = useState<{ id: string; fullName: string } | null>(null);
  const [rows, setRows] = useState<Remark[]>([]);
  const [kind, setKind] = useState('Appreciation');
  const [note, setNote] = useState('');
  const [staffOnly, setStaffOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const doSearch = async () => {
    if (search.trim().length < 2) return;
    try { setResults(asList(await api.get(`/api/students?search=${encodeURIComponent(search.trim())}&take=8`))); } catch { setResults([]); }
  };

  const pick = async (s: { id: string; fullName: string }) => {
    setStudent(s); setResults([]);
    try { setRows(await remarksApi.forStudent(s.id)); } catch { setRows([]); }
  };

  const add = async () => {
    if (!student || !note.trim()) return;
    setBusy(true); setMsg(null);
    try {
      await remarksApi.create({ studentId: student.id, kind, note: note.trim(), staffOnly });
      setNote(''); setStaffOnly(false);
      setRows(await remarksApi.forStudent(student.id));
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not save.'); } finally { setBusy(false); }
  };

  const del = async (id: string) => {
    if (!student || !window.confirm('Delete this remark?')) return;
    await remarksApi.remove(id);
    setRows(await remarksApi.forStudent(student.id));
  };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Students</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Remarks</h1>
      </div>

      {!student ? (
        <div className="card" style={{ padding: 18, maxWidth: 520 }}>
          <label className="label">Find a student</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input className="input" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()} placeholder="Name or admission number" />
            <button className="btn" onClick={doSearch}>Search</button>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {results.map((s) => (
              <button key={s.id} className="btn" style={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => pick(s)}>
                {s.fullName} · {s.admissionNumber}{s.classLabel ? ` · ${s.classLabel}` : ''}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <strong style={{ flex: 1 }}>{student.fullName}</strong>
            <button className="btn" onClick={() => { setStudent(null); setRows([]); }}>Change student</button>
          </div>

          <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 560 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 'auto' }}>
                {REMARK_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={staffOnly} onChange={(e) => setStaffOnly(e.target.checked)} />
                Staff-only (parent won't see)
              </label>
            </div>
            <textarea className="input" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Write a remark…" style={{ marginBottom: 10, resize: 'vertical' }} />
            <button className="btn btn-accent" disabled={busy} onClick={add}>{busy ? 'Saving…' : 'Add remark'}</button>
            {msg && <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--ink-2)' }}>{msg}</span>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.length === 0
              ? <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>No remarks yet.</div>
              : rows.map((r) => (
                  <div key={r.id} className="card" style={{ padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{r.kind}</span>
                      {r.staffOnly && <span className="pill" style={{ fontSize: 11 }}>Staff-only</span>}
                      <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 'auto' }}>{formatDate(r.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 14 }}>{r.note}</div>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>— {r.authorName}</span>
                      <button className="btn" style={{ marginLeft: 'auto', padding: '2px 8px' }} onClick={() => del(r.id)}>✕</button>
                    </div>
                  </div>
                ))}
          </div>
        </>
      )}
    </div>
  );
}
